import React, { useMemo } from 'react';

import SearchInput from 'components/common/SearchInput/SearchInput';
import {
  STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE,
  STEP_SELECTOR_SEARCH_PLACEHOLDER,
} from 'constants/uiConstants';
import { GcVariety } from 'interfaces/IGlobalCatalogue';
import MergeClassNames from 'utils/MergeClassNames';

import OptionRow from './OptionRow';
import {
  filterVarietiesByName,
  PICKER_VARIETY_ID_ATTRIBUTE,
  varietyToMetaOption,
} from './stepConfig';

interface VarietyListPanelProps {
  varieties: GcVariety[];
  searchText: string;
  highlightedVariety: GcVariety | null;
  decideLaterLabel: string;
  onSearchChange: (value: string) => void;
  onSelectVariety: (variety: GcVariety, event: React.MouseEvent) => void;
  onDecideLater: (event: React.MouseEvent) => void;
  className?: string;
}

// Search + filtered variety list + empty state + "decide later" footer,
// shared by SeedVarietyTwoPanel's variety half and SeedVarietyFlyout.
const VarietyListPanel: React.FC<VarietyListPanelProps> = ({
  varieties,
  searchText,
  highlightedVariety,
  decideLaterLabel,
  onSearchChange,
  onSelectVariety,
  onDecideLater,
  className,
}) => {
  const filteredVarieties = useMemo(
    () => filterVarietiesByName(varieties, searchText),
    [varieties, searchText]
  );

  return (
    <div className={MergeClassNames('flex flex-1 flex-col p-3', className)}>
      <div className="flex-shrink-0 pb-3">
        <SearchInput
          value={searchText}
          onChange={onSearchChange}
          placeholder={STEP_SELECTOR_SEARCH_PLACEHOLDER}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-bright bg-white">
        {filteredVarieties.length === 0 ? (
          <div className="p-4 text-sm text-gray-muted">
            {STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE}
          </div>
        ) : (
          filteredVarieties.map((variety) => (
            <OptionRow
              key={variety.varietyId}
              navAttributes={{
                [PICKER_VARIETY_ID_ATTRIBUTE]: variety.varietyId,
              }}
              label={variety.varietyName}
              metaOption={varietyToMetaOption(variety)}
              isHighlighted={
                highlightedVariety?.varietyId === variety.varietyId
              }
              onClick={(event) => onSelectVariety(variety, event)}
            />
          ))
        )}
      </div>
      <div className="flex flex-shrink-0 items-center justify-end p-3 pb-0">
        <button
          type="button"
          className="w-full rounded-[var(--radius-button)] border border-gray-medium bg-white px-3 py-1.5 text-sm font-medium text-charcoal"
          onClick={onDecideLater}
        >
          {decideLaterLabel}
        </button>
      </div>
    </div>
  );
};

export default VarietyListPanel;
